// =============================================================================
// Hosted-agent operational alerts (#3039 — HERMES)
//
// Read/acknowledge only: alerts are RAISED by the budget quota (a window
// crossed its warning threshold or its cap) and the health sweep, never by
// hand. Acknowledging closes the alert; a recurrence raises a new one.
//
// Backend: routes/admin/hosted_agent_alerts.py, services/hosted_agents/alerts.py.
// =============================================================================

import { api } from "@/lib/api-client"

export type HostedAgentAlertKind =
  | "budget_threshold"
  | "budget_exceeded"
  | "agent_unreachable"
  | "run_failures"

export type HostedAgentAlertSeverity = "warning" | "critical"

export type HostedAgentAlertStatus = "open" | "acknowledged" | "resolved"

/** Facts the budget producer attaches (services/hosted_agents/quota.py). */
export type HostedAgentBudgetAlertFacts = {
  window?: "daily_tokens" | "monthly_tokens" | "monthly_sek" | string
  unit?: "tokens" | "sek" | string
  percent?: number
  used?: number
  limit?: number
  period?: string
  resets_at?: string
}

export type HostedAgentAlert = {
  id: string
  tenant_id: string
  agent_id: string
  kind: HostedAgentAlertKind
  severity: HostedAgentAlertSeverity
  status: HostedAgentAlertStatus
  title: string
  facts: HostedAgentBudgetAlertFacts & Record<string, unknown>
  dedupe_key: string
  created_at: string
  updated_at: string
  acknowledged_at?: string | null
  acknowledged_by?: string | null
  resolved_at?: string | null
  resolved_reason?: string | null
}

export type HostedAgentAlertsResponse = {
  alerts: HostedAgentAlert[]
  count: number
}

/** Open alerts tenant-wide (newest first); `status: "all"` includes history. */
export async function listHostedAgentAlerts(options?: {
  agentId?: string
  status?: "open" | "all"
}): Promise<HostedAgentAlertsResponse> {
  const params: Record<string, string> = {}
  if (options?.agentId) params.agent = options.agentId
  if (options?.status) params.status = options.status
  const response = await api.get("/admin/hosted-agents/alerts", { params })
  const data = response.data || response
  const alerts = Array.isArray(data?.alerts) ? (data.alerts as HostedAgentAlert[]) : []
  return { alerts, count: typeof data?.count === "number" ? data.count : alerts.length }
}

/** Close an open alert because an admin has seen it. */
export async function acknowledgeHostedAgentAlert(alertId: string): Promise<HostedAgentAlert> {
  const response = await api.post(`/admin/hosted-agents/alerts/${encodeURIComponent(alertId)}/ack`)
  return (response.data || response).alert as HostedAgentAlert
}
